import React, { useState, useEffect } from "react";
import { IoShareOutline, IoCloseOutline } from "react-icons/io5";

const IOS_BANNER_DISMISSED_KEY = "iosInstallBannerDismissed";

// iOS (Safari, and every other browser there since they all run on WebKit)
// never fires beforeinstallprompt -- Apple has never implemented that API.
// So "Add to Home Screen" on iPhone/iPad has to be a manual, always-visible
// set of instructions instead of an automatic button.
function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isRunningStandalone(): boolean {
  return (
    (window.navigator as any).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showIosBanner, setShowIosBanner] = useState(false);

  useEffect(() => {
    const handler = (event: any) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowInstallButton(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    if (
      isIos() &&
      !isRunningStandalone() &&
      localStorage.getItem(IOS_BANNER_DISMISSED_KEY) !== "true"
    ) {
      setShowIosBanner(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === "accepted") {
        console.log("User installed the app");
      } else {
        console.log("User dismissed the install prompt");
      }
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  const dismissIosBanner = () => {
    localStorage.setItem(IOS_BANNER_DISMISSED_KEY, "true");
    setShowIosBanner(false);
  };

  return (
    <>
      {showInstallButton && (
        <button
          onClick={handleInstall}
          className="fixed bottom-4 left-4 z-[90] bg-blue-500 text-white px-4 py-2 rounded-md shadow-lg"
        >
          Install App
        </button>
      )}

      {showIosBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t shadow-[0_-2px_10px_rgba(0,0,0,0.1)] px-4 py-3 flex items-center gap-3">
          <p className="flex-1 text-sm text-gray-700">
            Install this app on your phone: tap{" "}
            <IoShareOutline className="inline h-4 w-4 mb-1" /> then{" "}
            <span className="font-semibold">Add to Home Screen</span>.
          </p>
          <button
            onClick={dismissIosBanner}
            aria-label="Dismiss"
            className="shrink-0 text-gray-500"
          >
            <IoCloseOutline className="h-6 w-6" />
          </button>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
